import { QueryClient } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import type { Persister, PersistedClient } from '@tanstack/react-query-persist-client';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter as Router } from 'react-router-dom';

import './index.css';
import App from './App';
import { Auth0ProviderWithNavigate, AxiosInterceptor } from './pages/auth';
import reportWebVitals from './reportWebVitals';
import { store } from './store';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

// MUI DataGrid (and other flex layouts) can trigger the benign
// "ResizeObserver loop completed with undelivered notifications" error, which
// otherwise pops the dev error overlay. Deferring ResizeObserver callbacks to
// the next animation frame breaks the synchronous resize loop at the source
// without dropping any entries. Development only — we don't want to alter
// ResizeObserver timing (or mask errors) in production builds.
if (
  process.env.NODE_ENV === 'development' &&
  typeof window !== 'undefined' &&
  window.ResizeObserver
) {
  const NativeResizeObserver = window.ResizeObserver;
  window.ResizeObserver = class extends NativeResizeObserver {
    constructor(callback: ResizeObserverCallback) {
      super((entries, observer) => {
        window.requestAnimationFrame(() => callback(entries, observer));
      });
    }
  };
}

const CACHE_KEY = 'cms2-query-cache';
// Stay safely below the ~5 MB per-origin localStorage quota so setItem doesn't
// throw QuotaExceededError on large caches. If we exceed this, skip persisting
// this tick — the in-memory cache stays, we just don't carry it across reloads.
const MAX_PERSIST_BYTES = 4 * 1024 * 1024;

const localStoragePersister: Persister = {
  persistClient: (client: PersistedClient) => {
    try {
      const serialized = JSON.stringify(client);
      if (serialized.length > MAX_PERSIST_BYTES) {
        if (process.env.NODE_ENV !== 'production') {
          // Visible signal during development that the cache is outgrowing its budget.
          console.warn(
            `[QueryCache] Persist skipped: serialised cache is ${(serialized.length / 1024 / 1024).toFixed(1)} MB (limit ${MAX_PERSIST_BYTES / 1024 / 1024} MB). In-memory cache retained.`
          );
        }
        return;
      }
      localStorage.setItem(CACHE_KEY, serialized);
    } catch {
      // localStorage full or unavailable — silently discard
    }
  },
  restoreClient: () => {
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      return raw ? (JSON.parse(raw) as PersistedClient) : undefined;
    } catch {
      return undefined;
    }
  },
  removeClient: () => {
    try {
      localStorage.removeItem(CACHE_KEY);
    } catch {
      // ignore
    }
  },
};

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      gcTime: 30 * 60 * 1000,
      // Dedupe refetches on component remount / rapid navigation.
      // Mutations still invalidate explicitly; refetchInterval still ticks.
      // Individual queries can override with staleTime: 0 if mount-fresh is required.
      staleTime: 30 * 1000,
    },
  },
});

root.render(
  <Router>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{ persister: localStoragePersister, maxAge: 30 * 60 * 1000 }}
    >
      <Auth0ProviderWithNavigate>
        <AxiosInterceptor>
          <Provider store={store}>
            <App />
          </Provider>
        </AxiosInterceptor>
      </Auth0ProviderWithNavigate>
      <ReactQueryDevtools initialIsOpen={false} />
    </PersistQueryClientProvider>
  </Router>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
